/**
 * Finances — Control de Finanzas Personales e Ingresos por Cliente.
 * 
 * - Resumen del mes (Ingresos, Gastos, Balance Neto)
 * - Gráfico comparativo de flujo mensual
 * - Registro de ingresos y gastos con modal interactivo
 * - Historial y desglose por cliente
 */

import { useEffect, useState } from 'react';
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Plus,
  Trash2,
  Loader2,
  X,
  PieChart as PieChartIcon,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { formatCurrency, formatShortDate } from '@/lib/utils';
import { useAppStore } from '@/stores/appStore';
import type { Finance, FinanceSummary, Client } from '@/types';

const INCOME_CATEGORIES = [
  'Honorarios Mensuales',
  'Consultoría Estratégica',
  'Gestión de Ads',
  'Diseño / Branding',
  'Otro Ingreso',
];

const EXPENSE_CATEGORIES = [
  'Software / Herramientas (Canva, etc.)',
  'Publicidad / Pauta',
  'Equipamiento',
  'Subcontratación',
  'Impuestos / Banco',
  'Otros Gastos',
];

export function Finances() {
  const { currentPeriod, setCurrentPeriod } = useAppStore();
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [finances, setFinances] = useState<Finance[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal nuevo registro
  const [showModal, setShowModal] = useState(false);
  const [type, setType] = useState<'income' | 'expense'>('income');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState(INCOME_CATEGORIES[0]);
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [clientId, setClientId] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Carga inicial y cambio de período
  useEffect(() => {
    async function loadFinances() {
      setLoading(true);
      try {
        const [sumRes, finRes, cliRes] = await Promise.all([
          api.getFinanceSummary(currentPeriod),
          api.getFinances({ month: currentPeriod }),
          api.getClients(),
        ]);
        setSummary(sumRes as FinanceSummary);
        setFinances(finRes.finances as Finance[]);
        setClients(cliRes.clients as Client[]);
      } catch (err) {
        console.error('Error cargando finanzas:', err);
      } finally {
        setLoading(false);
      }
    }
    loadFinances();
  }, [currentPeriod]);

  // Manejador de cambio de tipo en el modal
  const handleTypeChange = (newType: 'income' | 'expense') => {
    setType(newType);
    setCategory(newType === 'income' ? INCOME_CATEGORIES[0] : EXPENSE_CATEGORIES[0]);
  };

  const handleCreateFinance = async (e: React.FormEvent) => {
    e.preventDefault();
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) return;

    setSubmitting(true);
    try {
      const res = await api.createFinance({
        type,
        category,
        description: description || null,
        amount: numAmount,
        date,
        client_id: clientId || null,
        is_recurring: isRecurring,
      });

      const clientObj = clients.find((c) => c.id === clientId);
      const newEntry: Finance = {
        id: res.id,
        type,
        category,
        description: description || null,
        amount: numAmount,
        date,
        client_id: clientId || null,
        client_name: clientObj?.name,
        is_recurring: isRecurring,
        created_at: new Date().toISOString(),
      };

      setFinances((prev) => [newEntry, ...prev]);

      // Actualizar resumen local
      if (summary) {
        const isInc = type === 'income';
        setSummary({
          ...summary,
          income: {
            total: summary.income.total + (isInc ? numAmount : 0),
            count: summary.income.count + (isInc ? 1 : 0),
          },
          expense: {
            total: summary.expense.total + (!isInc ? numAmount : 0),
            count: summary.expense.count + (!isInc ? 1 : 0),
          },
          balance: summary.balance + (isInc ? numAmount : -numAmount),
        });
      }

      // Reset
      setAmount('');
      setDescription('');
      setShowModal(false);
    } catch (err) {
      console.error('Error registrando finanza:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Seguro que deseas eliminar este registro?')) return;
    try {
      await api.deleteFinance(id);
      const itemToDelete = finances.find((f) => f.id === id);
      setFinances((prev) => prev.filter((f) => f.id !== id));

      if (summary && itemToDelete) {
        const isInc = itemToDelete.type === 'income';
        setSummary({
          ...summary,
          income: {
            total: summary.income.total - (isInc ? itemToDelete.amount : 0),
            count: summary.income.count - (isInc ? 1 : 0),
          },
          expense: {
            total: summary.expense.total - (!isInc ? itemToDelete.amount : 0),
            count: summary.expense.count - (!isInc ? 1 : 0),
          },
          balance: summary.balance - (isInc ? itemToDelete.amount : -itemToDelete.amount),
        });
      }
    } catch (err) {
      console.error('Error eliminando registro:', err);
    }
  };

  // Datos para el gráfico comparativo
  const chartData = [
    {
      name: currentPeriod,
      Ingresos: summary?.income.total || 0,
      Gastos: summary?.expense.total || 0,
      Balance: summary?.balance || 0,
    },
  ];

  return (
    <div className="space-y-6">
      {/* ─── Header ─── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Wallet className="text-primary" />
            Finanzas Personales
          </h1>
          <p className="text-muted-foreground text-sm">
            Control de ingresos por clientes, gastos operativos y balance mensual.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="month"
            value={currentPeriod}
            onChange={(e) => setCurrentPeriod(e.target.value)}
            className="rounded-md border bg-background px-3 py-1.5 text-sm font-medium"
          />

          <Button onClick={() => setShowModal(true)} className="gap-2">
            <Plus size={16} />
            Nuevo Movimiento
          </Button>
        </div>
      </div>

      {/* ─── KPI Cards ─── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Ingresos del Mes
            </CardTitle>
            <TrendingUp size={18} className="text-emerald-500" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-emerald-500">
              {formatCurrency(summary?.income.total || 0)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {summary?.income.count || 0} movimientos registrados
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Gastos del Mes
            </CardTitle>
            <TrendingDown size={18} className="text-red-500" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-red-500">
              {formatCurrency(summary?.expense.total || 0)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {summary?.expense.count || 0} movimientos registrados
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Balance Neto
            </CardTitle>
            <DollarSign size={18} className="text-primary" />
          </CardHeader>
          <CardContent>
            <p
              className={`text-2xl font-bold ${
                (summary?.balance || 0) >= 0 ? 'text-foreground' : 'text-red-500'
              }`}
            >
              {formatCurrency(summary?.balance || 0)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {(summary?.balance || 0) >= 0 ? 'Flujo positivo 🚀' : 'Atención a los gastos ⚠️'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ─── Gráfico Comparativo ─── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <PieChartIcon size={18} className="text-primary" />
            Flujo Financiero ({currentPeriod})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(val) => `$${val.toLocaleString()}`} />
                <Tooltip
                  formatter={(value: any) => [formatCurrency(Number(value)), '']}
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    borderColor: 'hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                />
                <Legend />
                <Bar dataKey="Ingresos" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Gastos" fill="#ef4444" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Balance" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* ─── Tabla de Movimientos ─── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Historial de Transacciones</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : finances.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Wallet className="mx-auto h-12 w-12 text-muted-foreground/30 mb-2" />
              <p>No hay movimientos registrados en {currentPeriod}.</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowModal(true)}
                className="mt-4 gap-2"
              >
                <Plus size={14} /> Registrar primer movimiento
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="pb-3 font-semibold">Tipo</th>
                    <th className="pb-3 font-semibold">Categoría</th>
                    <th className="pb-3 font-semibold">Descripción / Cliente</th>
                    <th className="pb-3 font-semibold">Fecha</th>
                    <th className="pb-3 font-semibold text-right">Monto</th>
                    <th className="pb-3 font-semibold text-right">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {finances.map((item) => (
                    <tr key={item.id} className="hover:bg-muted/30 transition-colors">
                      <td className="py-3">
                        <Badge variant={item.type === 'income' ? 'success' : 'destructive'}>
                          {item.type === 'income' ? 'Ingreso' : 'Gasto'}
                        </Badge>
                      </td>
                      <td className="py-3 font-medium">{item.category}</td>
                      <td className="py-3">
                        <div>
                          {item.description || '—'}
                          {item.client_name && (
                            <span className="block text-xs text-primary font-medium">
                              Cliente: {item.client_name}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 text-muted-foreground">
                        {formatShortDate(item.date)}
                      </td>
                      <td
                        className={`py-3 text-right font-mono font-semibold ${
                          item.type === 'income' ? 'text-emerald-500' : 'text-red-500'
                        }`}
                      >
                        {item.type === 'income' ? '+' : '-'}
                        {formatCurrency(item.amount)}
                      </td>
                      <td className="py-3 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(item.id)}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Modal Nuevo Movimiento ─── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-card p-6 shadow-xl border animate-in">
            <div className="flex items-center justify-between pb-3 border-b">
              <h2 className="text-lg font-bold">Registrar Movimiento</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowModal(false)}
                className="h-8 w-8"
              >
                <X size={16} />
              </Button>
            </div>

            <form onSubmit={handleCreateFinance} className="mt-4 space-y-4">
              {/* Selector Tipo */}
              <div className="grid grid-cols-2 gap-2 p-1 bg-muted rounded-lg">
                <button
                  type="button"
                  onClick={() => handleTypeChange('income')}
                  className={`py-2 text-sm font-semibold rounded-md transition-all ${
                    type === 'income'
                      ? 'bg-card text-emerald-500 shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  + Ingreso
                </button>
                <button
                  type="button"
                  onClick={() => handleTypeChange('expense')}
                  className={`py-2 text-sm font-semibold rounded-md transition-all ${
                    type === 'expense'
                      ? 'bg-card text-red-500 shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  - Gasto
                </button>
              </div>

              {/* Monto */}
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  Monto ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-base font-semibold focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Categoría */}
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  Categoría
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  {(type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {/* Cliente Asociado (opcional) */}
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  Cliente Vinculado (Opcional)
                </label>
                <select
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">Ninguno / Finanza personal</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Fecha */}
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  Fecha
                </label>
                <input
                  type="date"
                  required
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                />
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  Descripción o Nota
                </label>
                <input
                  type="text"
                  placeholder="Ej: Pago mensual por redes sociales..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                />
              </div>

              {/* Recurrente */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="recurring"
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                  className="rounded border"
                />
                <label htmlFor="recurring" className="text-xs text-muted-foreground">
                  Gasto o ingreso recurrente cada mes
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : null}
                  Guardar Movimiento
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
